import { Link } from "react-router-dom";
import Ornament from "@/components/Ornament";
import { Wine, ChefHat, Flame, Leaf, Quote, Star } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/36747296/pexels-photo-36747296.jpeg?auto=compress&cs=tinysrgb&w=1920";
const ABOUT_IMG =
  "https://images.pexels.com/photos/15183169/pexels-photo-15183169.jpeg?auto=compress&cs=tinysrgb&w=940";

const FEATURES = [
  {
    icon: Wine,
    title: "Vinuri Fine",
    desc: "Colecție selectată din cele mai renumite podgorii europene.",
  },
  {
    icon: ChefHat,
    title: "Chef de Renume",
    desc: "Preparat de mâna unui bucătar cu experiență internațională.",
  },
  {
    icon: Flame,
    title: "Atmosferă Unică",
    desc: "Un cadru intim, elegant, perfect pentru orice ocazie specială.",
  },
  {
    icon: Leaf,
    title: "Ingrediente Proaspete",
    desc: "Selectate zilnic din surse locale și internaționale de top.",
  },
];

const TESTIMONIALS = [
  {
    text: "O seară absolut memorabilă. Mâncarea, vinul, serviciul — totul a fost impecabil.",
    name: "Andrei M.",
    source: "Google Reviews",
  },
  {
    text: "Cel mai bun restaurant francez din București, fără niciun dubiu. Ne vom întoarce cu siguranță!",
    name: "Ioana P.",
    source: "TripAdvisor",
  },
  {
    text: "Atmosfera este magică, iar preparatele sunt de nivel Michelin. Recomand cu căldură.",
    name: "Radu C.",
    source: "Google Reviews",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-night/80 via-brand-night/70 to-brand-night" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(107,15,26,0.45) 0%, transparent 60%)",
          }}
        />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto py-32">
          <div className="reveal flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-14 bg-brand-gold/60" />
            <span className="text-[11px] tracking-[0.45em] uppercase text-brand-gold font-sans">
              Bine aţi venit la
            </span>
            <span className="h-px w-14 bg-brand-gold/60" />
          </div>

          <h1
            data-testid="hero-title"
            className="reveal font-serif text-6xl sm:text-7xl md:text-8xl text-brand-ivory leading-[0.95] tracking-tight"
          >
            La Maison{" "}
            <br />
            <span className="italic text-brand-gold">Dorée</span>
          </h1>

          <p className="reveal mt-8 font-serif italic text-lg md:text-2xl text-brand-ivory/75 max-w-2xl mx-auto">
            O experiență culinară franțuzească în inima Bucureștiului.
          </p>

          <div className="reveal mt-12">
            <Ornament />
          </div>

          <div className="reveal mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/rezervari"
              data-testid="hero-cta-reserve"
              className="inline-flex items-center justify-center px-10 py-4 bg-brand-gold text-brand-night font-sans text-[12px] tracking-[0.35em] uppercase hover:bg-brand-goldHover transition-all duration-300 min-w-[220px]"
            >
              Rezervă o masă
            </Link>
            <Link
              to="/meniu"
              data-testid="hero-cta-menu"
              className="inline-flex items-center justify-center px-10 py-4 border border-brand-gold text-brand-gold font-sans text-[12px] tracking-[0.35em] uppercase hover:bg-brand-gold hover:text-brand-night transition-all duration-300 min-w-[220px]"
            >
              Vezi Meniul
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-brand-gold/60">
          <span className="text-[10px] tracking-[0.4em] uppercase font-sans">Descoperă</span>
          <span className="h-12 w-px bg-gradient-to-b from-brand-gold/70 to-transparent" />
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="py-24 md:py-32 bg-brand-night relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="reveal lg:col-span-5 order-1">
              <div className="relative p-3 border border-brand-gold/30">
                <div className="absolute inset-2 border border-brand-gold/15 pointer-events-none" />
                <div
                  className="aspect-[4/5] w-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${ABOUT_IMG})` }}
                  role="img"
                  aria-label="Atmosferă fine dining La Maison Dorée"
                />
              </div>
              <div className="mt-6 flex items-center gap-4 text-brand-gold/70">
                <span className="h-px w-10 bg-brand-gold/40" />
                <span className="font-serif italic text-sm">— Fondat în 1932 —</span>
              </div>
            </div>

            {/* Text */}
            <div className="lg:col-span-7 order-2">
              <span className="reveal block text-[11px] tracking-[0.4em] uppercase text-brand-gold mb-5 font-sans">
                Povestea noastră
              </span>
              <h2 className="reveal font-serif text-4xl sm:text-5xl md:text-6xl text-brand-ivory leading-[1.05] tracking-tight">
                O Pasiune Pentru
                <br />
                <span className="italic text-brand-gold">Rafinament</span>
              </h2>

              <div className="reveal mt-10 space-y-6 max-w-xl font-sans text-[16px] leading-[1.85] text-brand-ivory/75">
                <p>
                  La Maison Dorée s-a născut dintr-o dragoste profundă pentru
                  gastronomia franceză și dorința de a aduce eleganța Parisului
                  în centrul istoric al Bucureștiului.
                </p>
                <p>
                  Bucătarul nostru șef, format la Lyon și Paris, creează în
                  fiecare seară preparate care celebrează tradițiile culinare
                  europene, folosind ingrediente proaspete, selectate cu grijă.
                </p>
                <p>
                  Vă invităm să descoperiți un spațiu unde fiecare detaliu — de
                  la decor la farfurie — este gândit pentru a vă oferi o
                  experiență de neuitat.
                </p>
              </div>

              <div className="reveal mt-10 inline-flex items-center gap-4">
                <span className="font-serif italic text-3xl text-brand-gold">
                  Chef Antoine Laurent
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section
        data-testid="features-section"
        className="py-24 md:py-32 bg-brand-deep border-t border-b border-brand-gold/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="reveal block text-[11px] tracking-[0.4em] uppercase text-brand-gold mb-4 font-sans">
              Experienţa Maison
            </span>
            <h2 className="reveal font-serif text-3xl sm:text-4xl md:text-5xl text-brand-ivory">
              Patru piloni ai eleganţei
            </h2>
            <div className="reveal mt-8">
              <Ornament />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-gold/15">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`feature-card-${i}`}
                  className="reveal bg-brand-deep p-8 md:p-10 group hover:bg-brand-night transition-colors duration-500"
                >
                  <div className="flex items-center justify-center w-14 h-14 border border-brand-gold/40 mx-auto mb-6 group-hover:border-brand-gold transition-colors duration-500">
                    <Icon size={22} className="text-brand-gold" strokeWidth={1.3} />
                  </div>
                  <h3 className="text-center font-serif text-xl text-brand-gold mb-3">
                    {f.title}
                  </h3>
                  <p className="text-center font-sans text-sm text-brand-ivory/65 leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-brand-night">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-center mb-16">
            <span className="reveal block text-[11px] tracking-[0.4em] uppercase text-brand-gold mb-4 font-sans">
              Vocile invitaţilor noştri
            </span>
            <h2 className="reveal font-serif text-3xl sm:text-4xl md:text-5xl text-brand-ivory">
              <span className="italic text-brand-gold">Recenzii</span> & impresii
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                data-testid={`testimonial-${i}`}
                className="reveal relative bg-brand-panel border border-brand-gold/15 p-8 md:p-10 hover:border-brand-gold/40 transition-colors duration-500"
              >
                <Quote
                  size={36}
                  className="absolute -top-4 left-8 text-brand-gold bg-brand-panel px-1"
                  strokeWidth={1.2}
                  fill="currentColor"
                />
                <div className="flex gap-1 mb-5 text-brand-gold">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Star key={idx} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="font-serif italic text-lg leading-relaxed text-brand-ivory/85">
                  “{t.text}”
                </p>
                <div className="mt-8 pt-6 border-t border-brand-gold/15">
                  <p className="font-sans text-sm text-brand-ivory">{t.name}</p>
                  <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-brand-gold/70 mt-1">
                    {t.source}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-24 bg-brand-deep border-t border-brand-gold/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="reveal block text-[11px] tracking-[0.4em] uppercase text-brand-gold mb-5 font-sans">
            Vă aşteptăm
          </span>
          <h2 className="reveal font-serif text-4xl sm:text-5xl md:text-6xl text-brand-ivory leading-tight">
            O seară la <span className="italic text-brand-gold">Maison Dorée</span>
          </h2>
          <p className="reveal mt-6 font-serif italic text-lg text-brand-ivory/65 max-w-xl mx-auto">
            Pentru ocazii speciale sau pur și simplu pentru o seară de neuitat.
          </p>
          <div className="reveal mt-10">
            <Link
              to="/rezervari"
              data-testid="home-bottom-cta-reserve"
              className="inline-flex items-center justify-center px-12 py-4 bg-brand-gold text-brand-night font-sans text-[12px] tracking-[0.35em] uppercase hover:bg-brand-goldHover transition-colors duration-300"
            >
              Rezervă acum
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
